import React, { useState, useEffect, useRef } from "react";
import { Popover, Typography, TextField, Box, IconButton } from "@mui/material";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { isBlank } from "../../../../../utils";

const EditEventUrlCell = ({ params }) => {
  const { row, id, api } = params;

  // State for primary and alternative event URLs
  const [primaryEventUrl, setPrimaryEventUrl] = useState("");
  const [alternativeEventUrl, setAlternativeEventUrl] = useState("");

  // State for displayed URL
  const [displayedUrl, setDisplayedUrl] = useState("");

  // State for the popover and ref for the container
  const [anchorEl, setAnchorEl] = useState(null);
  const containerRef = useRef(null);

  // Refs for the input fields
  const primaryInputRef = useRef(null);
  const alternativeInputRef = useRef(null);

  // Function to get the correct event URL
  const getEventUrl = () => {
    if (!isBlank(alternativeEventUrl)) return alternativeEventUrl;
    if (!isBlank(row.alternativeEventUrl)) return row.alternativeEventUrl;
    if (!isBlank(row.EditedEntityLink?.href)) return row.EditedEntityLink.href;
    if (!isBlank(row.NewEntityLink?.href)) return row.NewEntityLink.href;
    if (!isBlank(row.entityLink?.href)) return row.entityLink.href;
    return "";
  };

  // Sync the state with the row values when the row changes,
  // but only if the popover is not open
  useEffect(() => {
    if (!anchorEl) {
      const initialPrimaryUrl =
        row.EditedEntityLink?.href ??
        row.NewEntityLink?.href ??
        row.entityLink?.href ??
        "";
      const initialAlternativeUrl = row.alternativeEventUrl || "";

      setPrimaryEventUrl(initialPrimaryUrl);
      setAlternativeEventUrl(initialAlternativeUrl);
      setDisplayedUrl(getEventUrl());
    }
  }, [row, anchorEl]);

  // Function to close the popover and update the UI
  const handleClosePopover = () => {
    setAnchorEl(null);

    // Update the database through the DataGrid API
    const fieldToUpdate = row.entityLink ? "EditedEntityLink" : "NewEntityLink";
    const newPrimaryValue = { href: primaryEventUrl, id: row.entityLink?.id };
    api.setEditCellValue({ id, field: fieldToUpdate, value: newPrimaryValue }, true);
    api.setEditCellValue({ id, field: "entityLink", value: newPrimaryValue }, true);
    api.setEditCellValue({ id, field: "alternativeEventUrl", value: alternativeEventUrl }, true);

    // Update the displayed URL
    setDisplayedUrl(getEventUrl());
  };

  // Unified key down handler for both inputs
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Prevent default to avoid premature commit with old values
      e.preventDefault();
      // Commit changes by closing the popover
      handleClosePopover();
    } else if (e.key === "Tab") {
      e.preventDefault();
      // Switch focus between inputs on Tab key
      if (e.target === primaryInputRef.current) {
        alternativeInputRef.current && alternativeInputRef.current.focus();
      } else if (e.target === alternativeInputRef.current) {
        primaryInputRef.current && primaryInputRef.current.focus();
      }
    }
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? "edit-event-url-popover" : undefined;

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          aria-label="edit"
          onClick={() => setAnchorEl(containerRef.current)}
          sx={{ padding: "4px" }}
        >
          <EditOutlinedIcon sx={{ fontSize: 20 }} color="primary" />
        </IconButton>
        <Typography>{displayedUrl}</Typography>
      </Box>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <Box sx={{ padding: 2, width: 400}}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>Primary Event URL:</Typography>
          <TextField
            value={primaryEventUrl}
            onChange={(e) => setPrimaryEventUrl(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            inputRef={primaryInputRef}
            onKeyDown={handleKeyDown}
          />
          <Typography  sx={{ marginTop: 1, fontSize: "14px", fontWeight: 600 }}>
            Alternative Event URL:
          </Typography>
          <TextField
            value={alternativeEventUrl}
            onChange={(e) => setAlternativeEventUrl(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            inputRef={alternativeInputRef}
            onKeyDown={handleKeyDown}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default React.memo(EditEventUrlCell);
